import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import { ButtonWithIcon, Tag } from '@blueprint/ui';

type GalleryProps = {
    hasArrowsOnImage?: boolean;
};

type SlideControlsContainerProps = {
    isVisible: boolean;
};

const Wrapper = styled('div')(css`
    position: relative;
    width: 100%;
    height: 100%;
`);

const Container = styled('div', {
    shouldForwardProp: isPropValid
})<GalleryProps>(({ theme, hasArrowsOnImage }) => {
    return css`
        position: relative;
        width: 100%;
        height: 100%;
        touch-action: pan-y;
        -webkit-tap-highlight-color: transparent;

        ${!hasArrowsOnImage &&
        `
            ${theme.mq.large} {
                width: calc(100% - 10rem);
                left: 50%;
                transform: translateX(-50%);
            }
        `}
    `;
});

const Viewport = styled('div')(css`
    cursor: pointer;
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    transform: translate3d(0, 0, 0);
`);

const SlideContainer = styled('ol')<{ hasMultipleSlides: boolean }>(
    ({ hasMultipleSlides }) => css`
        position: relative;
        display: flex;
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        list-style: none;
        user-select: none;
        will-change: transform;

        /*
        When we have multiple slides then we also have a cloned item as the first
        item within the slide container. For viewport offset calculations, it's
        simplest to allow the first real slide to be located at viewport offset 0px.
        We can achieve this by shifting the whole container to the left a single
        slide's width.
        */
        ${hasMultipleSlides && `margin-left: -100%;`}
    `
);

const Placeholder = styled('div')(
    ({ theme }) => css`
        display: flex;
        position: relative;
        width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
        background: ${theme.colors['grey-stone']};
    `
);

const ProgressIndicator = styled('div')(({ theme }) => {
    return css`
        position: absolute;
        bottom: ${theme.spacing['8px']};
        left: 50%;
        transform: translateX(-50%);
        pointer-events: none;

        ${theme.mq.medium} {
            bottom: ${theme.spacing['16px']};
        }

        ${Tag} {
            display: inline-flex;
            align-items: center;
            background-color: ${theme.colors['purple-dark-opacity80']};
            color: ${theme.colors.white};
            font-weight: ${theme.type.normal};
            font-size: 1rem;
            line-height: 1.375;
            padding: ${theme.spacing['4px']} ${theme.spacing['8px']}
                ${theme.spacing['4px']} ${theme.spacing['8px']};

            ${theme.mq.large} {
                font-size: 1.125rem;
                padding: ${theme.spacing['8px']};
            }

            svg {
                display: inline-block;
                margin-right: ${theme.spacing['4px']};
            }
        }
    `;
});

const Button = styled(ButtonWithIcon, {
    shouldForwardProp: isPropValid
})<GalleryProps>(({ theme, hasArrowsOnImage }) => {
    return css`
        bottom: 0;
        width: 2.75rem;
        height: 2.75rem;
        min-width: 0;
        background: ${theme.colors.white};
        border: none;
        margin-bottom: auto;
        margin-top: auto;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 0;

        > svg {
            bottom: 0;
            display: block;
            left: 0;
            margin: auto;
            position: absolute;
            right: 0;
            top: 0;
        }

        &:hover:not(:active) {
            transform: translateY(-1px);
            opacity: 1;
        }

        ${theme.mq.medium} {
            opacity: ${hasArrowsOnImage ? '0.8' : '1'};
            pointer-events: auto;
        }
    `;
});

const ButtonPrev = styled(Button)(({ theme }) => {
    return css`
        left: ${theme.spacing['16px']};
    `;
});

const ButtonNext = styled(Button)(({ theme }) => {
    return css`
        right: ${theme.spacing['16px']};
    `;
});

const SlideControlsContainer = styled('div')<SlideControlsContainerProps>(
    ({ isVisible, theme: { mq, spacing } }) => css`
        position: absolute;
        right: ${spacing['16px']};
        bottom: ${spacing['16px']};
        display: none;

        ${mq.large} {
            ${isVisible &&
            `
                display: block;
            `}
        }

        > * {
            position: absolute;
            bottom: 0;
            right: 0;
        }
    `
);

export {
    Wrapper,
    Container,
    Viewport,
    SlideContainer,
    Placeholder,
    ProgressIndicator,
    ButtonPrev,
    ButtonNext,
    SlideControlsContainer
};
